import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useLanguage } from "../state/language";
import { Button } from "../components/Button";
import { Modal } from "../components/Modal";
import type { Pack, Player } from "../domain/types";
import {
  deletePlayerGroup,
  deriveStatus,
  getActiveSession,
  getCardsByPackAndLanguage,
  getPackById,
  listPlayerGroups,
  putSession,
  savePlayerGroup,
  setPackProgress,
  touchPlayerGroup,
  type PlayerGroup,
} from "../db";
import { createSession } from "../engine/session";
import { modeStyle } from "../components/modeTheme";

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export default function Players() {
  const { packId } = useParams<{ packId: string }>();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { lang, t } = useLanguage();
  const language = lang ?? "uk";

  const [pack, setPack] = useState<Pack | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // Saved groups state — list + currently-loaded id (for "overwrite" UX).
  const [savedGroups, setSavedGroups] = useState<PlayerGroup[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [activeGroupName, setActiveGroupName] = useState<string>("");

  // Save dialog state.
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  // Load pack + saved groups + handle resume.
  useEffect(() => {
    if (!packId) return;
    let cancelled = false;
    (async () => {
      const p = (await getPackById(packId)) ?? null;
      if (cancelled) return;
      setPack(p);
      if (searchParams.get("resume") === "1") {
        const active = await getActiveSession();
        if (active && active.packId === packId && !cancelled) {
          nav(`/session/${active.id}`, { replace: true });
          return;
        }
      }
      const groups = await listPlayerGroups();
      if (!cancelled) setSavedGroups(groups);
    })();
    return () => {
      cancelled = true;
    };
  }, [packId, nav, searchParams]);

  function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setPlayers((prev) => [...prev, { id: uid(), name: trimmed }]);
    setName("");
  }

  function remove(id: string) {
    setPlayers((prev) => prev.filter((p) => p.id !== id));
  }

  async function loadGroup(g: PlayerGroup) {
    // Replace current list with the group's players (give them fresh IDs so
    // they're treated as a brand-new session even if names match).
    setPlayers(g.players.map((p) => ({ id: uid(), name: p.name })));
    setActiveGroupId(g.id);
    setActiveGroupName(g.name);
    await touchPlayerGroup(g.id);
    setSavedGroups(await listPlayerGroups());
  }

  async function removeGroup(g: PlayerGroup) {
    if (!confirm(`${t("delete_group_confirm")} «${g.name}»`)) return;
    await deletePlayerGroup(g.id);
    if (activeGroupId === g.id) {
      setActiveGroupId(null);
      setActiveGroupName("");
    }
    setSavedGroups(await listPlayerGroups());
  }

  function openSaveDialog() {
    if (players.length === 0) return;
    // Pre-fill name with the active group's name (so the host can overwrite
    // by tapping Save) or auto-derive from the first two players.
    const auto = activeGroupName || derivePresetName(players);
    setSaveName(auto);
    setSaveOpen(true);
  }

  async function commitSave() {
    const trimmed = saveName.trim();
    if (!trimmed || players.length === 0) return;
    // Overwrite if the trimmed name matches an existing group; else create new.
    const match = savedGroups.find((g) => g.name === trimmed);
    const saved = await savePlayerGroup({
      id: match?.id ?? activeGroupId ?? undefined,
      name: trimmed,
      players: players.map((p) => ({ id: p.id, name: p.name })),
    });
    setActiveGroupId(saved.id);
    setActiveGroupName(saved.name);
    setSavedGroups(await listPlayerGroups());
    setSaveOpen(false);
  }

  async function start() {
    if (!pack || players.length < 2) return;
    setBusy(true);
    const cards = await getCardsByPackAndLanguage(pack.id, language);
    if (cards.length === 0) {
      setBusy(false);
      alert(t("no_packs"));
      return;
    }
    // Auto-touch the active group so it floats to the top of the saved list
    // for the next session, but DON'T overwrite — saving is explicit.
    if (activeGroupId) await touchPlayerGroup(activeGroupId);
    const sess = createSession({
      modeId: pack.modeId,
      packId: pack.id,
      language,
      players,
      packCards: cards,
    });
    await putSession(sess);
    await setPackProgress({
      packId: pack.id,
      status: deriveStatus(0, sess.totalCards),
      progressPct: 0,
      cardsSeen: 0,
      totalCards: sess.totalCards,
      lastSessionId: sess.id,
      updatedAt: Date.now(),
    });
    nav(`/session/${sess.id}`, { replace: true });
  }

  const overwriting = useMemo(
    () => savedGroups.some((g) => g.name === saveName.trim()),
    [savedGroups, saveName]
  );

  return (
    <div className="pp-screen px-5" style={pack ? modeStyle(pack.modeId) : undefined}>
      <header className="flex items-center justify-between pt-4">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          ← {t("cta_back")}
        </Button>
        <h2 className="font-display text-xl font-bold">{t("players_title")}</h2>
        <div className="w-12" />
      </header>

      {/* SAVED GROUPS */}
      {savedGroups.length > 0 && (
        <section className="mt-4">
          <h3 className="text-xs uppercase tracking-wider opacity-70 mb-2">
            {t("saved_groups_title")}
          </h3>
          <div className="flex flex-col gap-2">
            {savedGroups.map((g) => {
              const active = activeGroupId === g.id;
              return (
                <div
                  key={g.id}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 border ${
                    active ? "bg-white/15 border-white/30" : "bg-white/5 border-white/10"
                  }`}
                >
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => loadGroup(g)}
                  >
                    <div className="font-semibold truncate">{g.name}</div>
                    <div className="text-[11px] opacity-60 truncate mt-0.5">
                      {g.players.length} · {g.players.map((p) => p.name).join(", ")}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => loadGroup(g)}
                    className="text-xs px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 shrink-0"
                  >
                    {t("load_group_cta")}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeGroup(g)}
                    aria-label={t("delete_group_cta")}
                    className="text-white/50 hover:text-red-300 text-lg shrink-0 px-1"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CURRENT LIST */}
      <div className="mt-6 text-3xl font-display font-bold">
        {t("players_counter", { n: players.length })}
        {activeGroupName && (
          <span className="block text-xs uppercase tracking-wider opacity-60 mt-1">
            ↳ {activeGroupName}
          </span>
        )}
      </div>

      <form
        className="mt-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input
          className="flex-1 rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-lg outline-none focus:border-white/40"
          placeholder={t("players_placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoCapitalize="words"
          autoComplete="off"
          maxLength={32}
        />
        <Button size="md" disabled={!name.trim()} type="submit">
          +
        </Button>
      </form>

      <ul className="mt-4 flex-1 overflow-auto flex flex-col gap-2">
        {players.map((p, i) => (
          <li
            key={p.id}
            className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 animate-phase-pop"
            style={{ animationDelay: `${i * 30}ms` }}
          >
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center font-bold">
              {p.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 truncate">{p.name}</div>
            <button
              className="text-white/50 hover:text-white text-xl px-3"
              aria-label={t("cta_delete")}
              onClick={() => remove(p.id)}
              type="button"
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <div className="pt-4 pb-2 sticky bottom-0 flex flex-col gap-2">
        {players.length < 2 && (
          <p className="text-center text-sm text-white/60">{t("players_min_warning")}</p>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={openSaveDialog}
            disabled={players.length === 0}
          >
            💾 {t("save_group_cta")}
          </Button>
          <Button
            variant="primary"
            size="md"
            className="flex-1"
            disabled={players.length < 2 || busy}
            onClick={start}
          >
            {t("cta_start")}
          </Button>
        </div>
      </div>

      {/* SAVE GROUP DIALOG */}
      <Modal
        open={saveOpen}
        title={t("save_group_dialog_title")}
        onClose={() => setSaveOpen(false)}
      >
        <input
          autoFocus
          className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-lg outline-none focus:border-white/40"
          placeholder={t("save_group_name_placeholder")}
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          maxLength={48}
        />
        <div className="mt-3 text-xs opacity-70">
          {players.length} · {players.map((p) => p.name).join(", ")}
        </div>
        <div className="mt-5 flex gap-3">
          <Button variant="ghost" fullWidth onClick={() => setSaveOpen(false)}>
            {t("cta_cancel")}
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={commitSave}
            disabled={!saveName.trim()}
          >
            {overwriting ? t("save_group_replace") : t("cta_save")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/** Generate a friendly default name when the host hasn't given one. */
function derivePresetName(players: Player[]): string {
  const names = players.slice(0, 2).map((p) => p.name);
  if (names.length === 0) return "";
  const date = new Date();
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${names.join(" + ")} · ${dd}.${mm}`;
}
